import React, { useMemo } from "react";
import axios from "axios";
import { useQuery } from "react-query";
import { colors2 } from "oolib";
import { UI_CAPTION } from "oolib/dist/v2/components/Typo2";

import TableElem from "@/components/cards/TableElem";
import OverlayScrollbar from "@/components/UI/OverlayScrollbar";
import { WidgetEmptyState, WidgetErrorState, WidgetLoadingState } from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { WIDGET_MAX_HEIGHT } from "../../../../../constants";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { CENTRE_COVERAGE_ENDPOINT, DATASETS, isMadTenant } from "../madSettings";

/**
 * MAD - Centre Assessment Summary — one row per MAD centre that has a CO /
 * CHO: the organisers, the centre's children, and how many of them have a
 * BASELINE assessment in at least one / all three of the templates and have
 * cleared the ASER numeracy / literacy baseline.
 *
 * All of it comes ready-made from one endpoint (getCentreCoverage): the
 * organisers from the profiles tagged CO / CHO, the children from MAD's
 * allocation feed through each volunteer's centre, the assessment columns
 * from the templates' baseline docs through the volunteer tag's centre. The
 * whole table is in hand at once, so every column sorts on the frontend.
 *
 * Not affected by the report's or the widget's filters: a centre's numbers
 * are a fact about the centre.
 */
const col = ({ colId, headerDisplay, fn = "numerical", props }) => ({
  colId,
  headerDisplay,
  comp: "TableText",
  valuePath: colId,
  sortConfig: { fn, path: colId },
  ...(props ? { props } : {}),
});

// A share of the centre's children, shown as "42%" but sorted on the number
// behind it — the cell reads `<colId>Display`, the sort reads `<colId>`.
const pctCol = ({ colId, headerDisplay }) => ({
  colId,
  headerDisplay,
  comp: "TableText",
  valuePath: `${colId}Display`,
  sortConfig: { fn: "numerical", path: colId },
  props: { color: colors2.grey60 },
});

const TABLE_CONFIG = {
  rowIdPath: "centreId",
  compactHeader: true,
  colWidths: { centre: 260, coChos: 220, children: 110, attemptedAny: 150, attemptedAnyPct: 90, attemptedAll: 150, attemptedAllPct: 90, clearedNumeracy: 150, clearedLiteracy: 150 },
  cellData: [
    { ...col({ colId: "centre", headerDisplay: "Centre", fn: "alphabetical", props: { typoComp: "UI_BODY_SEMIBOLD_SM", color: colors2.grey80 } }), defaultSort: true },
    col({ colId: "coChos", headerDisplay: "CO / CHO", fn: "alphabetical" }),
    col({ colId: "children", headerDisplay: "Children" }),
    col({ colId: "attemptedAny", headerDisplay: "Attempted ≥ 1 baseline" }),
    pctCol({ colId: "attemptedAnyPct", headerDisplay: "%" }),
    col({ colId: "attemptedAll", headerDisplay: "Attempted all 3 baselines" }),
    pctCol({ colId: "attemptedAllPct", headerDisplay: "%" }),
    col({ colId: "clearedNumeracy", headerDisplay: "Cleared ASER numeracy" }),
    col({ colId: "clearedLiteracy", headerDisplay: "Cleared ASER literacy" }),
  ],
};

const useCentreCoverage = (enabled) =>
  useQuery(["madCentreCoverage"], () => axios.get(CENTRE_COVERAGE_ENDPOINT).then((res) => res.data.data || []), {
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const Component = ({ id, widgetIdx }) => {
  const ready = isMadTenant();
  const coverage = useCentreCoverage(ready);
  // the two shares, of the centre's children; a centre with none shows "—"
  const rows = useMemo(
    () =>
      (coverage.data || []).map((row) => {
        const pct = (n) => (row.children ? Math.round((n / row.children) * 100) : null);
        const show = (p) => (p === null ? "—" : `${p}%`);
        const attemptedAnyPct = pct(row.attemptedAny), attemptedAllPct = pct(row.attemptedAll);
        return { ...row, attemptedAnyPct, attemptedAnyPctDisplay: show(attemptedAnyPct), attemptedAllPct, attemptedAllPctDisplay: show(attemptedAllPct) };
      }),
    [coverage.data]
  );

  const status = !ready ? "success" : coverage.status;
  useUpdateLoadedWidgetsTracker({ status, widgetIdx, hasData: rows.length > 0, widgetId: id });

  if (!ready) return <WidgetEmptyState display="This widget is not set up for this platform" />;

  return (
    // The Priorities widget's shell: a row flex box capped at the widget
    // height, the scroller its one child, everything inside the scroller.
    <div style={{ maxHeight: `${WIDGET_MAX_HEIGHT}px`, position: "relative", display: "flex" }}>
      <OverlayScrollbar style={{ flex: 1 }} overlay={false}>
        <div style={{ padding: "1.6rem" }}>
          {status === "loading" ? (
            <WidgetLoadingState />
          ) : status === "error" ? (
            <WidgetErrorState error={coverage.error} />
          ) : rows.length === 0 ? (
            <WidgetEmptyState display="No centre has a CO / CHO yet" />
          ) : (
            <>
              <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginBottom: "0.8rem" }}>
                {rows.length} {rows.length === 1 ? "centre" : "centres"} with a CO / CHO. Baseline assessments only.
              </UI_CAPTION>
              <TableElem id={`${id}:centreCoverage`} status="success" data={rows} tableConfig={TABLE_CONFIG} />
            </>
          )}
        </div>
      </OverlayScrollbar>
    </div>
  );
};

// MAD's widget, on MAD only — no other tenant sees it in the picker.
export const optionDef = {
  display: "MAD - Centre Assessment Summary",
  icon: "Table",
  desc: "Per centre with a CO / CHO: children, how many have baseline assessments, and how many cleared the ASER numeracy / literacy baseline.",
  isAvailableToAdd: isMadTenant,
};

export const defaultConfig = { props: {} };

// The templates this widget reads, for the report's filter sidebar (see the
// container's processRawConfigFn).
export const processRawConfigFn = (widgetConfig) => ({
  ...widgetConfig,
  props: { ...widgetConfig.props, contentTypes: Object.keys(DATASETS) },
});
